import React, { useState } from 'react';

import { useApolloClient, useMutation } from '@apollo/client';
import { Link, Spinner, Box, Text, Flex, Button } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';

import { createFormElements } from '../../../../../Form';
import { resendPhoneVerificationCode } from '../../../../../gql/mutations/resendPhoneVerificationCode';
import { verifyPhone } from '../../../../../gql/mutations/verifyPhone';
import { getMonolithUrls } from '../../../../../Navigation/utils/monolithUrls';
import { EButtonVariant, EColor } from '../../../../../Theme';
import { tracker } from '../../../../../utilities/tracker';
import {
  TVerificationCodeData,
  VerificationCodeSchema,
} from '../../../../lib/schema';
import { ECodeType, getCodeError } from '../../../../utils';
import { formatPhoneNumber } from '../utils';

const { Form, FormTextInput } = createFormElements<TVerificationCodeData>();

interface CollectVerifyUnifiedPhoneProps {
  phone: string;
  onBack: () => void;
}

export const CollectVerifyUnifiedPhone = ({
  phone,
  onBack,
}: CollectVerifyUnifiedPhoneProps) => {
  const { helpCenterUrl } = getMonolithUrls();
  const [hasResent, setHasResent] = useState(false);
  const client = useApolloClient();

  const methods = useForm<TVerificationCodeData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(VerificationCodeSchema),
    defaultValues: { code: '' },
  });

  const { setError, control, handleSubmit: hookHandleSubmit } = methods;

  const code = useWatch({ name: 'code', control });

  const [verifyPhoneMutation, { loading: isVerifyPhoneLoading }] =
    useMutation(verifyPhone);
  const [resendCode, { loading: isResendCodeLoading }] = useMutation(
    resendPhoneVerificationCode
  );

  const handleResend = async () => {
    if (phone) {
      try {
        await resendCode();
      } catch (error) {
        tracker.track('Resend Code Failed', {
          resendCodeError: true,
          errorString: JSON.stringify(error),
        });
      } finally {
        setHasResent(true);
        setTimeout(() => {
          setHasResent(false);
        }, 5000);
      }
    }
  };

  const handleSubmit = async (data: TVerificationCodeData) => {
    try {
      const { data: verifyPhoneData } = await verifyPhoneMutation({
        variables: data,
      });

      if (verifyPhoneData?.collectVerifyPhone?.successful) {
        tracker.track('Phone Number Verified', {
          success: true,
        });

        await client.refetchQueries({
          include: [
            'webGetUnifiedUserQuery',
            'webCurrentUserV2NewQuery',
            'webCurrentUserV2Query',
          ],
        });

        onBack();
      } else {
        const { messages } = verifyPhoneData?.collectVerifyPhone ?? {};

        if (messages?.length) {
          const errorMessage =
            getCodeError(ECodeType.SMS, messages?.[0]?.message ?? '') ??
            'Something went wrong';

          setError('code', {
            message: errorMessage,
          });

          tracker.track('Phone Number Verified', {
            success: false,
          });
          tracker.track('Verify Phone Failed', {
            verifyPhoneError: true,
            errorString: errorMessage,
          });
        }
      }
    } catch (error) {
      setError('code', {
        message: 'Something went wrong',
      });
    }
  };

  const formattedPhone = formatPhoneNumber(phone);

  return (
    <Box width="100%">
      <Text color={EColor.Neutral950} fontSize="12px">
        We sent a text message with your verification code to
      </Text>
      <Text color={EColor.Black} fontSize="12px" fontWeight={700} mb={6}>
        {formattedPhone}
      </Text>
      <Form {...methods} onSubmit={handleSubmit}>
        <FormTextInput
          _placeholder={{
            fontSize: '16px',
            letterSpacing: '0.32px',
            color: EColor.Neutral35,
          }}
          color={EColor.Black}
          fontSize="16px"
          height="56px"
          inputMode="numeric"
          name="code"
          placeholder="Enter code"
        />
        <Flex fontSize="14px" gap={2} my={4}>
          <Button
            bg="transparent"
            border="none"
            color={EColor.Black}
            fontSize="14px"
            fontWeight={400}
            height="auto"
            lineHeight="16px"
            padding="0"
            textDecoration="underline"
            textTransform="none"
            type="button"
            variant="link"
            onClick={handleResend}
          >
            {hasResent ? 'Resent' : 'Resend'}
            {isResendCodeLoading && <Spinner size="xs" />}
          </Button>
          <Link
            color={EColor.Black}
            fontSize="14px"
            fontWeight={400}
            height="auto"
            href={helpCenterUrl}
            lineHeight="16px"
            px={0}
            target="_blank"
            textDecoration="underline"
            variant="link"
          >
            Get support
          </Link>
        </Flex>
        <Box height="86px" my={4} width="100%" /> {/* spacer */}
        <Button
          fontSize="16px"
          fontWeight={700}
          height="54px"
          isDisabled={!code || isVerifyPhoneLoading || isResendCodeLoading}
          isLoading={isVerifyPhoneLoading || isResendCodeLoading}
          lineHeight="16px"
          mt="24px"
          type="submit"
          variant={EButtonVariant.BRAND_PRIMARY}
          width="100%"
        >
          Continue
        </Button>
      </Form>
    </Box>
  );
};
